/**
 * @fileoverview V2 Ingestion Shell — Full-screen layout with floating panels.
 *
 * Layout:
 *   Top bar (back button + matter name)
 *   ├─ Left stepper (floating)
 *   ├─ Center content (scrollable)
 *   └─ Right assist sidebar (floating, resizable)
 *
 * All three columns share the same vertical padding so containers align.
 */
import { skipToken } from '@reduxjs/toolkit/query';
import { ArrowLeft, FlaskConical, ToggleRight } from 'lucide-react';
import React, { useCallback, useRef, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';

import { useGetMatterDetailsQuery } from '@/services/api/mattersService';

import { Stepper } from '../navigation';
import { STEPS } from '../navigation/steps';
import { AssistSidebar } from '../sidebar';
import { useIngestion } from '../state/IngestionProvider';
import {
  BasicsStep,
  CaseInfoStep,
  ClientInformationStep,
  CompanionCasesStep,
  CoverageStep,
  DocumentsStep,
  LiabilityPoliceReportStep,
  NotesStep,
  ReviewStep,
  TreatmentStep,
} from '../steps';

function renderStep(stepId: string | undefined): React.JSX.Element {
  switch (stepId) {
    case 'basics':
      return <BasicsStep />;
    case 'case_info':
      return <CaseInfoStep />;
    case 'client_info':
      return <ClientInformationStep />;
    case 'companions':
      return <CompanionCasesStep />;
    case 'liability':
      return <LiabilityPoliceReportStep />;
    case 'treatment':
      return <TreatmentStep />;
    case 'coverage':
      return <CoverageStep />;
    case 'documents':
      return <DocumentsStep />;
    case 'notes':
      return <NotesStep />;
    case 'review':
      return <ReviewStep />;
    default:
      return (
        <div className="flex h-64 items-center justify-center text-sm text-zinc-400">
          {stepId ?? 'Unknown'} — not implemented yet
        </div>
      );
  }
}

function StepContent(): React.JSX.Element {
  const { state } = useIngestion();
  const step = STEPS[state.stepIndex];

  return renderStep(step?.id);
}

const TopBar = (): React.JSX.Element => {
  const navigate = useNavigate();
  const { state } = useIngestion();
  const { data: matter } = useGetMatterDetailsQuery(state.matterId ?? skipToken);

  const matterName = matter?.name;
  const firmId = matter?.firm?.id;
  const firmName = matter?.firm?.name;

  function switchToV1(): void {
    if (state.matterId) {
      navigate(`/matters/${state.matterId}/ingestion/v1`);
      return;
    }

    navigate('/matters/ingestion/v1');
  }

  return (
    <div className="mx-4 mb-2 mt-4">
      <div className="flex items-center gap-3 rounded-full border border-zinc-100 bg-white px-4 py-2 shadow-sm">
        <button
          type="button"
          onClick={() => navigate('/matters')}
          className="rounded-full p-1 text-zinc-400 transition-colors hover:bg-zinc-100 hover:text-zinc-700"
        >
          <ArrowLeft className="h-4 w-4" />
        </button>

        <div className="flex min-w-0 flex-1 items-center gap-2">
          {firmName && firmId && (
            <Link
              to={`/firms/${firmId}`}
              className="shrink-0 text-xs font-medium text-zinc-400 transition-colors hover:text-zinc-600 hover:underline"
            >
              {firmName}
            </Link>
          )}

          {firmName && matterName && <span className="text-zinc-300">/</span>}

          {matterName && state.matterId && (
            <Link
              to={`/matters/${state.matterId}`}
              className="truncate text-xs font-semibold text-zinc-700 transition-colors hover:text-zinc-900 hover:underline"
            >
              {matterName}
            </Link>
          )}

          {!matterName && !firmName && (
            <span className="text-xs text-zinc-400">New Matter</span>
          )}
        </div>

        <button
          type="button"
          onClick={switchToV1}
          className="flex items-center gap-1.5 rounded-full border border-zinc-200 px-2.5 py-1 text-[11px] font-medium text-zinc-500 transition-colors hover:border-zinc-300 hover:text-zinc-700"
        >
          <ToggleRight className="h-3.5 w-3.5 text-blue-500" />
          V2
        </button>
      </div>
    </div>
  );
}

function clampSidebarWidth(width: number): number {
  return Math.min(Math.max(width, 260), 600);
}

export const IngestionShell = (): React.JSX.Element => {
  const { isAssistOpen: assistOpen, setIsAssistOpen: setAssistOpen } = useIngestion();
  const [sidebarWidth, setSidebarWidth] = useState(480);
  const isResizing = useRef(false);

  const handleMouseDown = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>): void => {
      event.preventDefault();
      isResizing.current = true;

      const startX = event.clientX;
      const startWidth = sidebarWidth;

      function handleMouseMove(mouseEvent: MouseEvent): void {
        if (!isResizing.current) {
          return;
        }

        const delta = startX - mouseEvent.clientX;
        setSidebarWidth(clampSidebarWidth(startWidth + delta));
      }

      function handleMouseUp(): void {
        isResizing.current = false;
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
        document.body.style.cursor = '';
        document.body.style.userSelect = '';
      }

      document.body.style.cursor = 'col-resize';
      document.body.style.userSelect = 'none';
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [sidebarWidth],
  );

  return (
    <div className="fixed inset-0 flex flex-col overflow-hidden bg-zinc-50">
      <TopBar />

      <div className="flex flex-1 gap-3 overflow-hidden px-4 pb-4 pt-2">
        <div className="shrink-0">
          <Stepper />
        </div>

        <main className="relative min-w-0 flex-1 overflow-y-auto">
          {!assistOpen && (
            <button
              type="button"
              onClick={() => setAssistOpen(true)}
              className="absolute right-2 top-2 z-10 flex items-center gap-1.5 rounded-full border border-zinc-200 bg-white px-3 py-1.5 text-xs font-medium text-zinc-600 shadow-sm transition-colors hover:border-zinc-300 hover:text-zinc-800"
            >
              <FlaskConical className="h-3.5 w-3.5 text-blue-500" />
              Assist
            </button>
          )}

          <div className="mx-auto max-w-4xl px-4 pb-6">
            <div className="rounded-2xl border border-zinc-100 bg-white px-8 py-8 shadow-md">
              <StepContent />
            </div>
          </div>
        </main>

        {assistOpen && (
          <div className="relative shrink-0" style={{ width: sidebarWidth }}>
            <button
              type="button"
              aria-label="Resize assist sidebar"
              onMouseDown={handleMouseDown}
              className="absolute bottom-0 left-0 top-0 z-10 w-1.5 cursor-col-resize transition-colors hover:bg-blue-200 active:bg-blue-300"
            />
            <AssistSidebar onClose={() => setAssistOpen(false)} />
          </div>
        )}
      </div>
    </div>
  );
}
